import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import Modal from '../Modal/Modal';
import { PrimaryButton } from '../Button/Button';
import NamedLink from '../NamedLink/NamedLink';

import css from './SafetyDisclaimerModal.module.css';

const STORAGE_KEY = 'usedemoto_safety_warning_accepted';
const noop = () => {};

/**
 * Community safety warning & disclaimer modal with an "I Accept" confirmation.
 * Prompts visitors once on their first session about off-road use, safety,
 * meetup precautions, and payment security.
 */
const SafetyDisclaimerModal = props => {
  const {
    id = 'SafetyDisclaimerModal',
    className,
    rootClassName,
    onManageDisableScrolling = noop,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const accepted = window.localStorage.getItem(STORAGE_KEY);
        if (!accepted) {
          setIsOpen(true);
        }
      }
    } catch (e) {
      // Ignore localStorage access restrictions
    }
  }, []);

  const handleAccept = () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(STORAGE_KEY, 'true');
      }
    } catch (e) {
      // Ignore
    }
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isOpen) {
    return null;
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <Modal
      id={id}
      containerClassName={classes}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={handleClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={intl.formatMessage({
        id: 'SafetyDisclaimerModal.close',
        defaultMessage: 'Close',
      })}
    >
      <div className={css.headerRow}>
        <span className={css.warningIcon} aria-hidden="true">
          🛡️
        </span>
        <h2 className={css.title}>
          <FormattedMessage
            id="SafetyDisclaimerModal.title"
            defaultMessage="UsedEmoto Community & Safety Notice"
          />
        </h2>
      </div>

      <p className={css.intro}>
        <FormattedMessage
          id="SafetyDisclaimerModal.intro"
          defaultMessage="Welcome to UsedEmoto, the dedicated marketplace for electric motorcycles, parts, and gear. Please review our community standards before browsing or transacting:"
        />
      </p>

      <div className={css.guidelinesList}>
        <div className={css.guidelineItem}>
          <span className={css.itemIcon} aria-hidden="true">
            ⚡
          </span>
          <div className={css.itemContent}>
            <div className={css.itemTitle}>
              <FormattedMessage
                id="SafetyDisclaimerModal.offRoadTitle"
                defaultMessage="Off-Road & Legal Regulations"
              />
            </div>
            <p className={css.itemText}>
              <FormattedMessage
                id="SafetyDisclaimerModal.offRoadText"
                defaultMessage="Many high-performance e-motos are engineered specifically for off-road trails and private tracks. Always check state and local regulations regarding street legality, registration, and required safety gear (including a DOT/ECE certified helmet)."
              />
            </p>
          </div>
        </div>

        <div className={css.guidelineItem}>
          <span className={css.itemIcon} aria-hidden="true">
            📍
          </span>
          <div className={css.itemContent}>
            <div className={css.itemTitle}>
              <FormattedMessage
                id="SafetyDisclaimerModal.meetupTitle"
                defaultMessage="Safe Local Meetups"
              />
            </div>
            <p className={css.itemText}>
              <FormattedMessage
                id="SafetyDisclaimerModal.meetupText"
                defaultMessage="For in-person deals, always meet in a well-lit public place (e.g. police station safe-exchange zone or shopping plaza). Inspect the bike condition and frame/serial numbers before completing the handoff. Never send upfront wire or cash app deposits."
              />
            </p>
          </div>
        </div>

        <div className={css.guidelineItem}>
          <span className={css.itemIcon} aria-hidden="true">
            📦
          </span>
          <div className={css.itemContent}>
            <div className={css.itemTitle}>
              <FormattedMessage
                id="SafetyDisclaimerModal.shippingTitle"
                defaultMessage="Nationwide Shipping & Buyer Protection"
              />
            </div>
            <p className={css.itemText}>
              <FormattedMessage
                id="SafetyDisclaimerModal.shippingText"
                defaultMessage="When purchasing items with shipping, always complete checkout directly through UsedEmoto to ensure payment protection and tracking."
              />
            </p>
          </div>
        </div>
      </div>

      <div className={css.actions}>
        <PrimaryButton className={css.acceptButton} onClick={handleAccept}>
          <FormattedMessage
            id="SafetyDisclaimerModal.acceptButton"
            defaultMessage="I Understand & Accept"
          />
        </PrimaryButton>
        <p className={css.footerNote}>
          <FormattedMessage
            id="SafetyDisclaimerModal.learnMore"
            defaultMessage="Want more details?"
          />
          <NamedLink
            name="CMSPage"
            params={{ pageId: 'trust-and-safety' }}
            className={css.safetyLink}
            onClick={handleAccept}
          >
            <FormattedMessage
              id="SafetyDisclaimerModal.trustLink"
              defaultMessage="Read our Trust & Safety Guide"
            />
          </NamedLink>
        </p>
      </div>
    </Modal>
  );
};

SafetyDisclaimerModal.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  rootClassName: PropTypes.string,
};

export default SafetyDisclaimerModal;
